/* GlobalNavRail wraps Astryx's SideNav to add the ContxtIQ-specific styling
   Astryx's neutral theme and component props can't express: the brand green
   accent, and the muted/near-white label contrast between inactive and
   selected items this rail's design depends on. Everything else (layout,
   collapse, focus rings, hover states) comes straight from SideNav/Button. */

/* ContxtIQ green, scoped to this rail only (see plan section 5 / decisions
   table — "Green" row). Astryx's own accent is `--color-accent`, which
   drives primary-button fills and focus rings on every already-migrated
   page; we deliberately do NOT touch it here.
   SideNavItem's selected icon renders via `Icon`'s `color="primary"` slot
   (SideNavItem.js: `renderIconSlot(displayIcon, { color: isSelected ?
   'primary' : ... })`), which the compiled astryx.css resolves to
   `color: var(--color-icon-primary)` (`.xtbr613` rule). Redefining that
   token here — instead of `--color-accent` — scopes the green to just the
   selected item's icon in this subtree.
   Confidence: HIGH for the selected-icon match — traced through
   SideNavItem.js -> Icon.js -> the compiled rule. Verified nothing else in
   the rail reads `--color-icon-primary`: unselected icons use
   `color="secondary"` (`--color-icon-secondary`, untouched), the built-in
   collapse button's icon inherits the button's own text color
   (`--color-text-primary`, untouched here), and the CTA is a stock Astryx
   `primary` Button with no override at all. Per product feedback from a live-app
   screenshot, the selected row's BACKGROUND is left as Astryx's default
   (`--color-neutral`) — only the icon goes green. */
.rail {
  /* ContxtIQ green — the rail's only branded color. */
  --color-icon-primary: light-dark(#0fa892, #19e6c0);

  /* SideNav sizes its sticky footer zone against its own height, so this
     wrapper has to hand it the full column height. Without it the wrapper
     shrinks to content and the footer packs up under the last nav item
     instead of resting at the bottom of the rail. */
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  /* AppShell's default `elevated` variant separates rail from content with a
     raised surface rather than a rule, so the rail's original right-hand
     divider has to be restored here. */
  border-right: 1px solid var(--color-border);
}

.rail > * {
  flex: 1;
  min-height: 0;
}

/* Astryx's SideNavItem always paints its label at `--color-text-primary`
   (near-white/near-black) regardless of selected state — there is no muted
   variant built in (confirmed: the label `<span>` carries no color rule of
   its own, and neither the neutral theme nor SideNavItem's `.selected`
   stylex block touches text color, only background and font-weight). Re-mute
   unselected rows to match the rail's original design (muted until current),
   keyed off the `aria-current="page"` attribute SideNavItem already sets for
   `isSelected` items rather than duplicating that logic here.
   Scoped to `.navItemGroup` — the primary/admin sections and the footer icon
   row — so the CTA button (a different slot entirely) is never touched by
   this rule. Confidence: HIGH, verified against SideNavItem.js and the
   compiled label-span rules (layout-only, no color). */
.navItemGroup {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  width: 100%;
}

.navItemGroup a:not([aria-current="page"]),
.navItemGroup button:not([aria-current="page"]) {
  --color-text-primary: var(--color-text-secondary);
}
